import { type PhysicsTuning, SIM_VERSION } from '../config/tuning';
import { decodeInputs, encodeInputs, unpackInput } from '../sim/input';
import type { TrackRuntime } from '../sim/trackQuery';
import { type SimEvent, spawnState, stepWorld } from '../sim/world';
import { hashHex } from '../track/prng';

/**
 * Lap records are per-tick input sequences from the spawn state. Replaying one
 * through the simulation reproduces the lap exactly: that drives ghosts now and
 * server-side verification later.
 */
export interface LapRecord {
  version: 1;
  date: string;
  trackHash: string;
  physicsHash: string;
  lapMs: number;
  sectorMs: number[];
  /** Which lap of the run this was (1 = first flying lap after a restart). */
  lapIndex: number;
  /** Tick at which the lap completed; inputs cover ticks 1..endTick. */
  endTick: number;
  /** RLE + base64 packed inputs from spawn through endTick. */
  inputs: string;
  createdAt: number;
}

/** Fingerprint of a car: its physics values and the sim code version that drives them. */
export function physicsHash(p: PhysicsTuning): string {
  const keys = Object.keys(p).sort() as (keyof PhysicsTuning)[];
  return hashHex(JSON.stringify([SIM_VERSION, keys.map((k) => [k, p[k]])]));
}

export function makeRecord(args: {
  date: string;
  trackHash: string;
  physics: PhysicsTuning;
  lapMs: number;
  sectorMs: number[];
  lapIndex: number;
  endTick: number;
  packedInputs: number[];
}): LapRecord {
  return {
    version: 1,
    date: args.date,
    trackHash: args.trackHash,
    physicsHash: physicsHash(args.physics),
    lapMs: args.lapMs,
    sectorMs: args.sectorMs.slice(),
    lapIndex: args.lapIndex,
    endTick: args.endTick,
    inputs: encodeInputs(args.packedInputs.slice(0, args.endTick)),
    createdAt: Date.now(),
  };
}

/** Per-tick pose of a replayed lap, for the ghost car and the live delta. */
export interface GhostTrack {
  /** Fractional tick (since spawn) at which the lap started / ended. */
  lapStart: number;
  lapEnd: number;
  /** Absolute tick of frames[0]. */
  firstTick: number;
  count: number;
  /** Interleaved per-tick values, STRIDE per frame. */
  frames: Float64Array;
  /** Lap time (ms) at which each whole metre of the lap was first reached. */
  timeAtMetre: Float64Array;
}

export const GHOST_STRIDE = 9;
// x, y, heading, wheelAngle, vx, ax, curbMask, rpm, s

export interface ReplayResult {
  completed: boolean;
  lapMs: number;
  sectorMs: number[];
  valid: boolean;
  endTick: number;
  ghost: GhostTrack | null;
}

/**
 * Replay a record from the spawn state. If `withGhost`, capture the pose of
 * every tick of the recorded lap.
 */
export function replayRecord(rt: TrackRuntime, p: PhysicsTuning, record: LapRecord, withGhost = true): ReplayResult {
  const job = replayJob(rt, p, record, withGhost);
  // A broken recording (or one made up): it simply doesn't replay.
  return job ? (job.step(Infinity) as ReplayResult) : { completed: false, lapMs: 0, sectorMs: [], valid: false, endTick: 0, ghost: null };
}

/** A replay that can run a slice at a time (see ReplayJob); null for a recording that can't be read. */
export function replayJob(rt: TrackRuntime, p: PhysicsTuning, record: LapRecord, withGhost = true): ReplayJob | null {
  let packed: number[];
  try {
    packed = decodeInputs(record.inputs);
  } catch {
    return null;
  }
  return new ReplayJob(rt, p, packed, record.lapIndex, withGhost);
}

export function replayInputs(rt: TrackRuntime, p: PhysicsTuning, packed: number[], lapIndex: number, withGhost = true): ReplayResult {
  return new ReplayJob(rt, p, packed, lapIndex, withGhost).step(Infinity) as ReplayResult;
}

/**
 * Replaying a recording a slice of ticks at a time: the same simulation, run to the end of the lap in
 * as many slices as it takes. Checking a new best this way spreads the work over the frames after the
 * finish line instead of freezing the one where it's crossed.
 */
export class ReplayJob {
  private state: ReturnType<typeof spawnState>;
  private events: SimEvent[] = [];
  private frames: number[] = [];
  private lapStart = -1;
  private firstTick = -1;
  private prevPose: number[];
  private i = 0;
  /** Set once the lap is done (or the recording ran out). */
  result: ReplayResult | null = null;

  constructor(
    private rt: TrackRuntime,
    private p: PhysicsTuning,
    private packed: number[],
    private lapIndex: number,
    private withGhost: boolean,
  ) {
    this.state = spawnState(rt, p);
    this.prevPose = poseOf(this.state);
  }

  /** Up to `ticks` more ticks; the result once there is one. */
  step(ticks: number): ReplayResult | null {
    if (this.result) return this.result;
    const { rt, p, packed, lapIndex, withGhost, state, events, frames } = this;
    const end = Math.min(packed.length, this.i + ticks);
    for (; this.i < end; this.i++) {
      events.length = 0;
      stepWorld(state, unpackInput(packed[this.i]), rt, p, events);
      for (const e of events) {
        if (e.type === 'lapStart' && e.lapIndex === lapIndex) {
          this.lapStart = e.time;
          this.firstTick = state.tick - 1;
          if (withGhost) {
            // The frame just before the crossing, so interpolation covers the start.
            pushFrame(frames, this.prevPose);
          }
        }
        if (e.type === 'lapComplete' && e.lapIndex === lapIndex) {
          if (withGhost) pushFrame(frames, poseOf(state));
          const ghost = withGhost ? buildGhost(rt, this.lapStart, e.endTime, this.firstTick, frames) : null;
          this.result = { completed: true, lapMs: e.ms, sectorMs: e.sectorMs, valid: e.valid, endTick: e.endTick, ghost };
          return this.result;
        }
      }
      if (withGhost && this.lapStart >= 0) pushFrame(frames, poseOf(state));
      this.prevPose = poseOf(state);
    }
    if (this.i >= packed.length) this.result = { completed: false, lapMs: 0, sectorMs: [], valid: false, endTick: state.tick, ghost: null };
    return this.result;
  }
}

function poseOf(state: ReturnType<typeof spawnState>): number[] {
  const c = state.car;
  const sf = c.surfaces;
  const curbMask = (sf[0] === 1 ? 1 : 0) | (sf[1] === 1 ? 2 : 0) | (sf[2] === 1 ? 4 : 0) | (sf[3] === 1 ? 8 : 0);
  return [c.x, c.y, c.heading, c.wheelAngle, c.vx, c.ax, curbMask, c.rpm, c.s];
}

function pushFrame(frames: number[], pose: number[]): void {
  for (let k = 0; k < GHOST_STRIDE; k++) frames.push(pose[k]);
}

function buildGhost(rt: TrackRuntime, lapStart: number, lapEnd: number, firstTick: number, frames: number[]): GhostTrack {
  const count = frames.length / GHOST_STRIDE;
  const metres = Math.ceil(rt.length) + 1;
  const timeAtMetre = new Float64Array(metres).fill(-1);
  let reached = 0;
  for (let f = 0; f < count; f++) {
    const t = firstTick + f;
    const lapMs = ((t - lapStart) * 1000) / 60;
    if (lapMs < 0) continue;
    const s = frames[f * GHOST_STRIDE + 8];
    // Ignore the wrap right after the line.
    const m = s > rt.length - 30 && lapMs < 3000 ? 0 : Math.floor(s);
    while (reached <= m && reached < metres) {
      timeAtMetre[reached] = lapMs;
      reached++;
    }
  }
  for (let m = reached; m < metres; m++) timeAtMetre[m] = ((lapEnd - lapStart) * 1000) / 60;
  return { lapStart, lapEnd, firstTick, count, frames: Float64Array.from(frames), timeAtMetre };
}

/** Interpolated ghost pose at a fractional tick. Returns false outside the lap. */
export function sampleGhost(g: GhostTrack, tick: number, out: number[]): boolean {
  const f = tick - g.firstTick;
  if (f < 0 || f > g.count - 1) return false;
  const i = Math.floor(f);
  const j = Math.min(i + 1, g.count - 1);
  const t = f - i;
  const a = i * GHOST_STRIDE;
  const b = j * GHOST_STRIDE;
  for (let k = 0; k < GHOST_STRIDE; k++) out[k] = g.frames[a + k] + (g.frames[b + k] - g.frames[a + k]) * t;
  // Heading must interpolate the short way round.
  let dh = g.frames[b + 2] - g.frames[a + 2];
  if (dh > Math.PI) dh -= Math.PI * 2;
  else if (dh < -Math.PI) dh += Math.PI * 2;
  out[2] = g.frames[a + 2] + dh * t;
  out[6] = g.frames[a + 6];
  return true;
}

/** Live delta (ms) against the ghost lap, at lap distance `s` metres after `elapsedMs`. */
export function deltaAt(g: GhostTrack, s: number, elapsedMs: number): number | null {
  const m = Math.floor(s);
  if (m < 0 || m >= g.timeAtMetre.length) return null;
  const ref = g.timeAtMetre[m];
  if (ref < 0) return null;
  const next = m + 1 < g.timeAtMetre.length ? g.timeAtMetre[m + 1] : ref;
  const refInterp = ref + (next - ref) * (s - m);
  return elapsedMs - refInterp;
}
